import React from 'react';
import BlogPost from '../../../components/BlogPost';

const DataBackupAndRecovery = () => (
  <BlogPost
    breadcrumbItems={[
      { path: '/', label: 'Home' },
      { path: '/blog', label: 'Blog' },
      { path: '/blog/cloud-security-series', label: 'Cloud Security Series' },
      {
        path: '/blog/cloud-security-series/data-backup-and-recovery',
        label: 'Data Backup and Recovery',
      },
    ]}
    title="Data Backup and Recovery"
    date="Oct 18, 2024"
    content={
      <>
        <section className="blog-post__section">
          <h2>Data Backup and Recovery</h2>
          <p>Cloud Infrastructure Security Series #5. 📦 Data Backup and Recovery</p>
          <p>
            In the original post we defined steps to implement a bullet-proof Cloud Infrastructure
            Security Posture. Now it's time to dive into the topic of 📦 Data Backup and Recovery.
          </p>
          <p>
            <strong>❓ How would you approach the implementation of the following?</strong>
            <br />
            – Establish Data Classification and Data Retention Policies;
            <br />– Establish Data Backup and Recovery Procedures.
          </p>
          <p>
            <strong>💪 Here is how, in just a few steps:</strong>
          </p>
          <ol>
            <li>
              Think of categories of data that your organization, systems, and applications operate,
              put them "on paper", and define sensitivity levels based on your understanding. Use
              guidance – usually the government agencies (examples:{' '}
              <a href="https://lnkd.in/e58zeiaj" target="_blank" rel="noopener noreferrer">
                link
              </a>
              ,{' '}
              <a href="https://lnkd.in/eseKYnkW" target="_blank" rel="noopener noreferrer">
                link
              </a>
              ) and educational institutions (example{' '}
              <a href="https://lnkd.in/eJsWBtaU" target="_blank" rel="noopener noreferrer">
                link
              </a>
              ) are very good at data classification, so use the publicly provided information as
              your template.
            </li>
            <li>
              For Data Retention Policy, define how long you want to store the following categories
              of information:
              <ul className="blog-post__indented-list">
                <li>Data in Cloud Storage (e.g. S3)</li>
                <li>Data in Databases</li>
                <li>Data on Disk Volumes</li>
                <li>Application Logs</li>
                <li>Load Balancer Logs</li>
                <li>Cloud Storage Access Logs</li>
                <li>Database Logs</li>
                <li>Cloud Service Logs (e.g. CloudTrail)</li>
                <li>Host Audit Logs</li>
              </ul>
              Some of the above data sources store your customers' data. Define its retention period
              taking your contractual responsibilities into account. Also, make sure to specify what
              you do upon contract termination (options are: delete all data immediately, or delete
              operational data immediately – keep data in backups, and for how long).
            </li>
            <li>
              Set data lifecycle policies on Cloud Storage, Disk Volumes, and Cloud Log Groups
              ("Data Storage Points") in accordance with your Data Retention Policy. It would also
              be a good idea to come up with data tiering structure, so it moves from{' '}
              <em>immediate</em> to <em>frequent</em> to <em>infrequent</em> access locations during
              its lifecycle. This will help save some data storage costs and will also show you
              exactly what data, and how much of it is <strong>really</strong> important.
            </li>
            <li>
              Review your data lifecycle and retention policies regularly as it is good for your
              company's Cloud Economics and IT compliance.
            </li>
            <li>
              Based on your knowledge about how data is stored and retained in your cloud
              environment, document recovery processes for each Data Storage Point. Make sure you
              can recover data within your RTO ("Recovery Time Objective") and RPO ("Recovery Point
              Objective") commitments.
            </li>
            <li>
              Test data recovery procedures regularly (at least every 6 months). You will be
              surprised how quickly they get outdated, so it is important to stay on top of it.
            </li>
          </ol>
          <p>See anything missing or wrong?</p>
        </section>
      </>
    }
    cta={{
      title: 'We Are Here to Help!',
      text: "Struggling to set up or simplify your SOC 2 compliance? Our expert team at DevSecOps, Inc. specializes in making compliance manageable and straightforward for lean startups. We've successfully helped numerous startups quickly and affordably achieve compliance without sacrificing agility.",
      buttonText: `Let's Talk`,
      buttonHref: '/contact-us',
    }}
    prevPost={{
      href: '/blog/cloud-security-series/data-encryption',
      label: 'Data Encryption',
    }}
    nextPost={{
      href: '/blog/vulnerability-management-system',
      label: 'Next: Vulnerability Management System',
    }}
  />
);

export default DataBackupAndRecovery;
